const STORAGE_KEY = "wn_ip_approx_location_v1";
const IPAPI_URL = "https://ipapi.co/json/";

let memoryCache = null;
let pendingLookup = null;
const fallbackStorage = new Map();

function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readRaw() {
  if (typeof sessionStorage !== "undefined") {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }
  return fallbackStorage.has(STORAGE_KEY)
    ? fallbackStorage.get(STORAGE_KEY)
    : null;
}

function writeRaw(value) {
  if (typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.setItem(STORAGE_KEY, value);
      return;
    } catch {
      return;
    }
  }
  fallbackStorage.set(STORAGE_KEY, value);
}

function removeRaw() {
  if (typeof sessionStorage !== "undefined") {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return;
  }
  fallbackStorage.delete(STORAGE_KEY);
}

function readStoredCache() {
  const raw = readRaw();
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!isRecord(parsed) || parsed.v !== 1) {
      return null;
    }
    if (parsed.status === "skip") {
      return { v: 1, status: "skip" };
    }
    if (parsed.status !== "ok" || !isRecord(parsed.location)) {
      return null;
    }
    const latitude = Number(parsed.location.latitude);
    const longitude = Number(parsed.location.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return null;
    }
    return {
      v: 1,
      status: "ok",
      location: {
        latitude,
        longitude,
        city: String(parsed.location.city || ""),
        region: String(parsed.location.region || ""),
        regionCode: String(parsed.location.regionCode || ""),
        countryName: String(parsed.location.countryName || ""),
      },
    };
  } catch {
    return null;
  }
}

function remember(record) {
  memoryCache = record;
  writeRaw(JSON.stringify(record));
}

function parseLocationPayload(payload) {
  if (!isRecord(payload) || payload.error === true) {
    return null;
  }
  const latitude = Number(payload.latitude);
  const longitude = Number(payload.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  return {
    latitude,
    longitude,
    city: typeof payload.city === "string" ? payload.city.trim() : "",
    region: typeof payload.region === "string" ? payload.region.trim() : "",
    regionCode:
      typeof payload.region_code === "string"
        ? payload.region_code.trim().toUpperCase()
        : "",
    countryName:
      typeof payload.country_name === "string"
        ? payload.country_name.trim()
        : "",
  };
}

async function lookupIpApproximateLocation() {
  if (memoryCache && memoryCache.status === "ok") {
    return memoryCache.location;
  }
  if (memoryCache && memoryCache.status === "skip") {
    return null;
  }

  const stored = readStoredCache();
  if (stored) {
    memoryCache = stored;
    return stored.status === "ok" ? stored.location : null;
  }

  if (pendingLookup) {
    return pendingLookup;
  }

  pendingLookup = (async () => {
    try {
      const response = await fetch(IPAPI_URL);
      if (!response.ok) {
        remember({ v: 1, status: "skip" });
        return null;
      }
      const payload = await response.json();
      const location = parseLocationPayload(payload);
      if (!location) {
        remember({ v: 1, status: "skip" });
        return null;
      }
      remember({ v: 1, status: "ok", location });
      return location;
    } catch {
      remember({ v: 1, status: "skip" });
      return null;
    } finally {
      pendingLookup = null;
    }
  })();

  return pendingLookup;
}

function resetIpApproximateLocationCacheForTests() {
  memoryCache = null;
  pendingLookup = null;
  removeRaw();
}

module.exports = {
  IP_APPROXIMATE_LOCATION_STORAGE_KEY: STORAGE_KEY,
  lookupIpApproximateLocation,
  resetIpApproximateLocationCacheForTests,
  parseLocationPayload,
};
